import { ChangeEvent, MouseEvent } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { DEAL_STATUS, formatDate, MEDIA_TYPE } from '../../../../util/deals-util';
import DealsTablePaginationActions from './DealsTablePaginationActions';
import Chip from '@mui/material/Chip';
import styled from '@emotion/styled';
import { DealOverviewDto } from '../../../../api/services/dto/deal-overview-dto';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { applicationService } from '../../../../api/services/application.service';
import { useAlert } from '../../../shared/AlertProvider';
import { roundToTwoDecimals } from '../../../../util/price-range-util';
import { ColumnKey } from './ColumnVisibilityButton';

type DealsTableProps = {
  dealList: DealOverviewDto[];
  total: number;
  page: number;
  rowsPerPage: number;
  handlePageChange: (newPage: number) => void;
  handleRowsPerPageChange: (newRowsPerPage: number) => void;
  visibleColumns: ColumnKey[];
};

const mediaTypeLabelMap: { [key: string]: string } = {
  BANNER: 'Banners',
  NATIVE: 'Native',
  VIDEO: 'Video',
  CWIRE_FORMAT: 'C Wire Formats',
};

const DealsTable = ({
  dealList,
  total,
  page,
  rowsPerPage,
  handlePageChange,
  handleRowsPerPageChange,
  visibleColumns,
}: DealsTableProps) => {
  const navigate = useNavigate();
  const { setAlert } = useAlert();

  const isColumnVisible = (columnKey: ColumnKey) => visibleColumns.includes(columnKey);

  const handleDuplicate = (id: number) => {
    applicationService.duplicateDeal(id).then((deal: any) => {
      console.log(`Duplicate successed ${deal.id}`)
      navigate(`../deal/edit/${deal.id}`)
    })
      .catch((err) => {
        setAlert({
          open: true,
          severity: 'error',
          title: err.message,
          message: err.options?.cause
        })
      });
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A', paddingLeft: '16px' }}>
            {isColumnVisible('ssp') && (
              <TableCell style={{ width: 100 }}>SSP</TableCell>
            )}
            {isColumnVisible('buyerSeatName') && (
              <TableCell style={{ width: 200}}>
                Buyer Seat/Member
              </TableCell>
            )}
            {isColumnVisible('buyerSeatId') && (
              <TableCell style={{ width: 100 }}>
                Buyer ID
              </TableCell>
            )}
            {isColumnVisible('dealId') && (
              <TableCell style={{ width: 100 }}>Deal ID</TableCell>
            )}
            {isColumnVisible('name') && (
              <TableCell >Deal Name</TableCell>
            )}
            {isColumnVisible('floorPrice') && (
              <TableCell style={{ width: 100 }}>Floor Price</TableCell>
            )}
            {isColumnVisible('mediaType') && (
              <TableCell style={{ width: 120 }}>Media Type</TableCell>
            )}
            {isColumnVisible('status') && (
              <TableCell style={{ width: 100}}>Status</TableCell>
            )}
            {isColumnVisible('created') && (
              <TableCell align="left" style={{ width: 110 }}>
                Created
              </TableCell>
            )}
            {isColumnVisible('actions') && (
              <TableCell align="right" style={{ width: 70 }}></TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {dealList?.length ? (
            dealList.map((deal: DealOverviewDto) => (
              <TableRow key={deal.id} data-testid={`deal-${deal.id}`}>
                {isColumnVisible('ssp') && (
                  <TableCell style={{ width: 100 }}>{deal.ssp}</TableCell>
                )}
                {isColumnVisible('buyerSeatName') && (
                  <TableCell style={{ width: 200 }}>{deal.buyerSeatName}</TableCell>
                )}
                {isColumnVisible('buyerSeatId') && (
                  <TableCell style={{ width: 100 }}>{deal.buyerSeatId}</TableCell>
                )}
                {isColumnVisible('dealId') && (
                  <TableCell style={{ width: 100 }}>{deal.xandrDealId || '--'}</TableCell>
                )}
                {isColumnVisible('name') && (
                  <TableCell>{deal.name}</TableCell>
                )}
                {isColumnVisible('floorPrice') && (
                  <TableCell style={{ width: 100 }}>
                    {deal.floorPrice ? `${roundToTwoDecimals(deal.floorPrice)} ${deal.currency}` : '--'}
                  </TableCell>
                )}
                {isColumnVisible('mediaType') && (
                  <TableCell style={{ width: 120 }}>
                    {
                      <Chip
                        label={
                          deal.formats && deal.formats.length > 0
                            ? mediaTypeLabelMap[deal.formats[0].type]
                            : '--'
                        }
                        className={`table-chip ${deal.formats[0].type === MEDIA_TYPE.CWIRE_FORMAT
                          ? 'cwire-chip'
                          : deal.formats[0].type === MEDIA_TYPE.VIDEO
                            ? 'video-chip'
                            : deal.formats[0].type === MEDIA_TYPE.NATIVE
                              ? 'native-chip'
                              : deal.formats[0].type === MEDIA_TYPE.BANNER
                                ? 'banner-chip'
                                : ''
                          }`}
                      />
                    }
                  </TableCell>
                )}
                {isColumnVisible('status') && (
                  <TableCell style={{ width: 100 }}>
                    {
                      <Chip
                        label={
                          deal.status === DEAL_STATUS.ACTIVE
                            ? 'Active'
                            : deal.status === DEAL_STATUS.DRAFT
                              ? 'Draft'
                              : deal.status === DEAL_STATUS.IN_CREATION
                                ? 'In creation'
                                : deal.status === DEAL_STATUS.ERROR
                                  ? 'Error'
                                  : ''
                        }
                        className={`table-chip ${deal.status === DEAL_STATUS.ACTIVE
                          ? 'deal-status-active'
                          : deal?.status === DEAL_STATUS.DRAFT
                            ? 'deal-status-draft'
                            : deal.status === DEAL_STATUS.IN_CREATION
                              ? 'deal-status-in-creation'
                              : deal.status === DEAL_STATUS.ERROR
                                ? 'deal-status-error'
                                : ''
                          }`}
                      />
                    }
                  </TableCell>
                )}
                {isColumnVisible('created') && (
                  <TableCell style={{ width: 110 }} align="left">
                    {formatDate(deal.createdDate)}
                  </TableCell>
                )}
                {isColumnVisible('actions') && (
                  <TableCell style={{ width: 110, whiteSpace: 'nowrap' }} align="left">

                    <IconButton
                      disableRipple
                      onClick={() => navigate(`edit/${deal.id}`)}
                      className="buyer-pro-action-button"
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      disableRipple
                      onClick={() => handleDuplicate(deal.id)}
                      className="buyer-pro-action-button"
                      title="Copy"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow className="no-matches">
              <TableCell colSpan={visibleColumns.length}>
                <NoMatches>No matches</NoMatches>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {dealList.length ? (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                colSpan={visibleColumns.length}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{ native: true }}
                onPageChange={(e: MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                  handlePageChange(newPage)
                }
                onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleRowsPerPageChange(parseInt(e.target.value, 10))
                }
                ActionsComponent={DealsTablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};

export default DealsTable;

const NoMatches = styled.div`
  font-size: 16px;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px;
`;

const SubCellText = styled.span`
  font-size: 12px;
  color: #444444;
  text-align: center;
`;